import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "src/environments/environment";

declare const grecaptcha: {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

export interface FormProtectionConfig {
  recaptchaEnabled: boolean;
  recaptchaSiteKey: string;
}

@Injectable({
  providedIn: "root",
})
export class FormProtectionService {
  private configPromise: Promise<FormProtectionConfig> | null = null;
  private scriptLoadPromise: Promise<void> | null = null;
  private loadedSiteKey = "";

  constructor(private readonly http: HttpClient) {}

  getConfig(): Promise<FormProtectionConfig> {
    if (!this.configPromise) {
      this.configPromise = firstValueFrom(
        this.http.get<FormProtectionConfig>(
          `${environment.apiBaseUrl}api/PublicConfig/form-protection`
        )
      ).catch(() => ({ recaptchaEnabled: false, recaptchaSiteKey: "" }));
    }

    return this.configPromise;
  }

  async getRecaptchaToken(action: string): Promise<string | null> {
    const config = await this.getConfig();
    if (!config.recaptchaEnabled || !config.recaptchaSiteKey) {
      return null;
    }

    await this.loadRecaptchaScript(config.recaptchaSiteKey);
    return grecaptcha.execute(config.recaptchaSiteKey, { action });
  }

  async isRecaptchaEnabled(): Promise<boolean> {
    const config = await this.getConfig();
    return config.recaptchaEnabled && !!config.recaptchaSiteKey;
  }

  private loadRecaptchaScript(siteKey: string): Promise<void> {
    if (this.scriptLoadPromise && this.loadedSiteKey === siteKey) {
      return this.scriptLoadPromise;
    }

    this.loadedSiteKey = siteKey;
    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector(
        'script[src*="google.com/recaptcha/api.js"]'
      );

      const onReady = () => {
        grecaptcha.ready(() => resolve());
      };

      if (existing) {
        onReady();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = onReady;
      script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }
}
